import React from 'react'
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone'
import PsychologyIcon from '@mui/icons-material/Psychology'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import JobCarousel from './JobCarousel'
import { useHandleChangeRoute } from 'data/hooks'

const VALUE_PROPS = [
  {
    id: 'direct-access',
    icon: ContactPhoneIcon,
    title: 'Talk to the Hospital, Directly',
    description:
      'Every listing includes verified contact info for in-house recruiters and decision-makers. No third-party agencies, no cold calls, no middlemen.',
  },
  {
    id: 'ai-powered',
    icon: PsychologyIcon,
    title: 'AI That Actually Helps You Decide',
    description:
      'Ask our AI assistant Stethi about any job — compensation, call schedule, culture — and get matched to roles based on your specialty, location, and career goals.',
  },
  {
    id: 'smart-alerts',
    icon: NotificationsActiveIcon,
    title: 'Never Miss the Right Role',
    description:
      'Set your filters once — specialty, location, salary, visa sponsorship — and get notified the moment a matching position is posted.',
  },
  {
    id: 'relocation-support',
    icon: HomeWorkIcon,
    title: 'Move with Confidence',
    description:
      'Get connected with vetted real estate agents who understand clinician relocations, plus exclusive rebates on your transaction.',
  },
]

const HowItWorks = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  return (
    <Box sx={{ backgroundColor: 'white' }}>
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: theme.palette.grey[900],
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Healthcare Job Search, Without the Noise
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.grey[600],
                fontSize: '1.1rem',
                lineHeight: 1.7,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Four things that make MyStethi different from everything else out there.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {VALUE_PROPS.map((prop) => {
              const Icon = prop.icon
              return (
                <Grid item xs={12} sm={6} key={prop.id}>
                  <Card
                    sx={{
                      height: '100%',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '16px',
                      backgroundColor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.palette.shadows.medium,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '12px',
                          backgroundColor: theme.palette.primary.light,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.palette.primary.dark,
                          mb: 3,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.grey[900],
                          mb: 1.5,
                          fontSize: '1.1rem',
                        }}
                      >
                        {prop.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.grey[700],
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        }}
                      >
                        {prop.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 6 } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleChangeRoute('/register/physicians')()}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                px: 5,
                py: 1.75,
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: theme.palette.shadows.colored.primary,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: theme.palette.shadows.colored.primaryHover,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>

      <JobCarousel />
    </Box>
  )
}

export default HowItWorks
